/**
 * Terminals (D343): the terminals its person opened to remote control in AI Workbench, as a list beside the open
 * one. A terminal opens only when chosen. Watching needs `terminals:read` on the machine and typing `terminals:write`
 * (with a recent sign-in), both allowed in Settings; turning a terminal's remote control on happens at the workbench.
 */

import { COMPANION_ERRORS, type ChannelCloseCode, type TerminalInfo } from '@aioe/shared';
import { Terminal } from '@gravity-ui/icons';
import { useState } from 'react';

import { ArtEmpty, Button, ErrorNote, StatusMark } from '../../components/ui.tsx';
import { useLoad } from '../../hooks.ts';
import { useMachine } from '../machineContext.ts';
import { refusalOf } from '../relayChannel.ts';
import type { Workbench } from '../useWorkbench.ts';
import { RemoteView } from './RemoteView.tsx';
import { TerminalPane } from './TerminalPane.tsx';

const tone = 'teal' as const;

function terminalWords(code: ChannelCloseCode, reason?: string): string {
  switch (refusalOf(reason)) {
    case COMPANION_ERRORS.remoteControlOff:
      return 'Remote control is off for this terminal. Its person turns it on in AI Workbench.';
    case COMPANION_ERRORS.terminalScope:
      return 'This machine does not allow terminals from the console. Allow it in Settings.';
    case COMPANION_ERRORS.terminalNotFound:
      return 'That terminal has closed on the workbench.';
    case COMPANION_ERRORS.terminalUnavailable:
      return 'This computer has no terminals to show.';
  }
  if (code === 'done') return 'The terminal has ended.';
  if (code === 'workbench_offline') return 'The workbench is not connected. This carries on when it is.';
  if (code === 'scope') return 'This machine does not allow terminals from the console. Allow it in Settings.';
  if (code === 'refused') return reason ? reason.replace(/^[a-z_]+:\s*/, '') : 'The workbench said no.';
  if (code === 'limit') return 'Too many open at once. Close one and open this again.';
  return 'The terminal closed.';
}

export function TerminalView({ w }: { w: Workbench }) {
  const m = useMachine();
  const s = m.scopes;
  const watch = s.has('terminals:read');
  const list = useLoad(
    async () => {
      if (!w.connected || !watch) return undefined;
      try {
        const r = await w.tryCall<{ terminals: TerminalInfo[] }>('terminals.list');
        return r ? { kind: 'list' as const, terminals: r.terminals } : { kind: 'too-old' as const };
      } catch (e) {
        return /-32001|scope/i.test((e as Error).message) ? { kind: 'scope' as const } : Promise.reject(e);
      }
    },
    [w.connected, watch],
    w.connected && watch ? 30_000 : undefined,
    { whenVisible: true },
  );
  const [open, setOpen] = useState<string>();

  if (!s.known && !s.hidden) return <RemoteView><p className="text-sm text-muted">Reading what this machine allows…</p></RemoteView>;
  if (!watch || list.data?.kind === 'scope')
    return (
      <RemoteView narrow>
        <ArtEmpty
          tone={tone}
          icon={<Terminal />}
          title="Terminals are not allowed for this machine"
          action={
            s.hidden ? undefined : (
              <Button size="sm" isPending={s.busy} onPress={() => void s.change(['terminals:read'], ['terminals:read'])}>
                Allow watching terminals
              </Button>
            )
          }
        >
          {s.hidden ? 'Its owner or an administrator can allow it.' : 'A terminal is a shell on the machine, so watching and typing are each allowed on purpose. Typing is in Settings, and needs a recent sign-in.'}
        </ArtEmpty>
        <ErrorNote error={s.error} />
      </RemoteView>
    );
  if (list.data?.kind === 'too-old')
    return (
      <RemoteView narrow>
        <ArtEmpty tone={tone} icon={<Terminal />} title="This workbench needs an update to show its terminals here">
          Once AI Workbench is updated on that computer, the terminals its person opens to remote control show here.
        </ArtEmpty>
      </RemoteView>
    );

  const terminals = list.data?.kind === 'list' ? list.data.terminals : [];
  const shown = terminals.find((t) => t.terminalId === open);
  return (
    <RemoteView fill>
      <ErrorNote error={w.connected ? list.error : undefined} />
      {!w.connected && !terminals.length ? (
        <ArtEmpty tone={tone} icon={<Terminal />} title="The workbench is not connected">
          Its terminals show here while it runs and is connected to your organisation.
        </ArtEmpty>
      ) : list.data && !terminals.length ? (
        <ArtEmpty tone={tone} icon={<Terminal />} title="No terminals open">
          Terminals opened in AI Workbench show here; its person turns remote control on for each one they want to share.
        </ArtEmpty>
      ) : (
        <div className="grid min-h-0 flex-1 gap-3 lg:grid-cols-[15rem_minmax(0,1fr)]">
          <ul className="m-0 flex list-none gap-1 overflow-x-auto p-0 [scrollbar-width:none] lg:flex-col lg:overflow-y-auto" aria-label="Terminals">
            {terminals.map((t) => {
              const current = t.terminalId === open;
              return (
                <li key={t.terminalId} className="shrink-0 lg:shrink">
                  <button
                    type="button"
                    disabled={!t.remote}
                    onClick={() => setOpen(t.terminalId)}
                    aria-current={current || undefined}
                    className={`w-full min-w-44 cursor-[var(--cursor-interactive)] rounded-lg px-3 py-2 text-left disabled:cursor-default ${current ? 'bg-surface shadow-sm' : 'hover:bg-surface-secondary'}`}
                  >
                    <span className="block truncate text-sm font-medium">{t.title || 'Terminal'}</span>
                    <span className="block text-xs text-muted">{!t.remote ? 'Not shared with remote control' : t.state === 'exited' ? 'Ended' : 'Running'}</span>
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="min-h-[22rem] min-w-0">
            {shown ? (
              <TerminalPane key={shown.terminalId} deviceId={w.deviceId} method="terminals.subscribe" params={{ terminalId: shown.terminalId }} type={s.has('terminals:write')} writeScope="terminals:write" words={terminalWords} />
            ) : (
              <div className="flex h-full items-center justify-center rounded-xl bg-surface-secondary p-6 text-center text-sm text-muted">
                {terminals.some((t) => t.remote) ? 'Choose a terminal to open it.' : 'None of these is shared with remote control yet. Its person shares a terminal from AI Workbench.'}
              </div>
            )}
          </div>
        </div>
      )}
      {shown ? null : <StatusLine watch={watch} type={s.has('terminals:write')} />}
    </RemoteView>
  );
}

function StatusLine({ watch, type }: { watch: boolean; type: boolean }) {
  return <StatusMark state={type ? 'connected' : watch ? 'idle' : 'disabled'} label={type ? 'You can watch and type' : 'You can watch; typing is allowed in Settings'} />;
}
